import React, { useCallback, useEffect, useState } from "react";
import { CookService } from "../../services/CookServices";
import CkMuiTable from "../Common/CkMuiTable/CkMuiTable";
import { CookEscalationInterface } from "../../interfaces/Cook/CookEscalationInterface";
import {
  Box,
  Button, Dialog, DialogContent, DialogActions, DialogTitle, FormControl,
  Grid, InputLabel,
  Link, MenuItem, Select,
  TableCell,
  TableRow, TextField,
} from "@mui/material";
import CookComment from "../../interfaces/Cook/CookComment";
import { formatDate, formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import CookEscalationDialog from "../Common/CookEscalationDialog";
import { useRecordContext, useRefresh } from "ra-core";
import { CookWorkStatus } from "../../enums/CookWorkStatus";
import { CookWorkStatusReasoning } from "../../enums/CookWorkStatusReasoning";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import EventBus from "../../EventBus";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { CookEscalationAction } from "../../enums/CookEscalationAction";
import { useAppSelector } from "../../store/hooks";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";
import { CookEscalationResolutions } from "../../enums/CookEscalationResolution.";
import Op from "../../interfaces/SequalizeFilters/SequalizeTypes";
import { CookEscalationResolutionsEnum } from "../../enums/CookEscalationResolutionsEnum";
import MarkNotFreeForWorkDialog from "../Common/MarkNotFreeForWorkDialog";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";
import { FilterIcon } from "../Svg";
import {
  FilterContainer,
  FIlterHeader,
  FilterSection,
  FilterTextField,
  SectionHeader,
  SectionTitle,
  WorkSpaceTitleRow,
} from "../../styles/commonStyles";
import TransactionButton from "../Common/TransactionButton";

const cookService = new CookService();

interface InputProps {
  cookId: number;
}

const getRefLink = (escalation: CookEscalationInterface): string => {
  if (escalation?.referenceModelName === 'ONE_MEAL_SERVICE') return `/#/OneMealService/${escalation?.referenceId}/show`;
  if (escalation?.referenceModelName === 'TRIAL_V3' || escalation?.referenceModelName === 'TRIALS') return `/#/Trials/${escalation?.referenceId}/show`;
  if (escalation?.referenceModelName === 'COOK_SUBSCRIPTION_V3' || escalation?.referenceModelName === 'SUBSCRIPTION') return `/#/UserBundles/${escalation?.referenceId}/show`;
  if (escalation?.referenceModelName === 'CHEF_FOR_PARTY_ORDER') return `/#/ChefForParty/${escalation?.referenceId}/show`;
  return '';
};

const CookEscalation = ({ cookId }: InputProps) => {
  const userDetails = useAppSelector((state) => state.userDetails.value);
  let groupsArray: string[] = userDetails.adminUser.accessGroups;
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  let [subData, setSubData] = useState<any>(null);
  const [cookEscalationData, setCookEscalationData] = useState<CookEscalationInterface[]>();
  const [escalationDialogBox, setOpenEscalationDialogBox] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [escalationDialogOpen, setEscalationDialogOpen] = useState<boolean>(false);
  const [freeToWork, setFreeToWork] = useState<boolean>(false);
  const [markNotFreeOpen, setMarkNotFreeOpen] = useState<boolean>(false);
  const [notFreeToWorkDialog, setNotFreeToWorkDialog] = useState<boolean>(false);
  const [selectedReasoning, setSelectedReasoning] = useState<CookWorkStatusReasoning | ''>('');
  const [addComment, setAddComment] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [topicProgressId, setTopicProgressId] = useState<string>('');
  const [escalationId, setEscalationId] = useState<number>();
  const [escalationType, setEscalationType] = useState<string>('');
  const [escalationResolution, setEscalationResolution] = useState<string>('');
  const record = useRecordContext();
  const refresh = useRefresh();

  const handleSubmit = () => {
    cookService
      .markFreeToWork(record?.cookFullProfile?.cookId, { comment })
      .then(() => {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Successfully marked free to work",
          type: CustomSnackbarTypes.SUCCESS,
        });
        setFreeToWork(false);
        setComment('');
        loadData();
      })
      .catch((err) => { console.log(err); })
      .finally(() => { refresh(); });
  };

  const fetchDetails = useCallback((limit: number, offset: number, filters: any) => {
    if (!cookId) return;
    cookService
      .getEscalations(cookId, limit, offset, filters)
      .then((res) => {
        setSubData(res);
        setCookEscalationData(res?.data);
      })
      .catch((err) => { console.log(err); });
  }, [cookId]);

  const loadData = useCallback(() => {
    let filters: any = {};
    if (status !== '') filters.actionByCook = status;
    if (type !== '') filters.resolution = type;
    if (topicProgressId === 'MANUAL') filters.topicProgressId = { [Op.eq.toString()]: null };
    if (topicProgressId === 'AUTOMATED') filters.topicProgressId = { [Op.not.toString()]: null };
    fetchDetails(rowsPerPage, page * rowsPerPage, filters);
  }, [fetchDetails, page, rowsPerPage, status, type, topicProgressId]);

  useEffect(() => { loadData(); }, [loadData]);

  const columns: CommonListColumn<CookEscalationInterface>[] = [
    { label: 'Id', field: 'id' },
    { label: 'Product', field: 'referenceModelName' },
    {
      label: 'Trigger Source',
      render: (escalation) => !escalation?.createdBy ? 'Customer' : (
        <Link href={`/#/AdminUsers/${escalation?.createdBy}/show`}>{escalation?.createdBy}</Link>
      ),
    },
    {
      label: 'Reference Link',
      render: (escalation) => {
        const link = getRefLink(escalation);
        return link !== '' ? <Link href={link}>{escalation?.referenceId}</Link> : <>{escalation?.referenceId}</>;
      },
    },
    { label: 'Escalation Reason', field: 'reason' },
    { label: 'Sub Reason', field: 'subReason' },
    { label: 'Resolution Type', field: 'resolution' },
    { label: 'Created At', nowrap: true, render: (e) => formatDateTimeWithSeconds(e?.createdAt) },
    { label: 'MANUAL/AUTOMATED', render: (e) => e?.topicProgressId ? 'AUTOMATED' : 'MANUAL' },
    { label: 'End Date', nowrap: true, render: (e) => e?.endDate ? formatDateTimeWithSeconds(e?.endDate) : '---' },
    { label: 'Escalation Status', field: 'actionByCook' },
    {
      label: 'Comments',
      render: (escalation) => (
        <CkMuiTable sx={{}} headers={["Name", "Comment", "TimeStamp"]} rowData={[]} isTablePagination={false}>
          {escalation?.comments?.map((comment: CookComment, idx: number) => (
            <TableRow key={"" + idx}>
              <TableCell>{comment?.name}</TableCell>
              <TableCell>{comment?.comment}</TableCell>
              <TableCell style={{ whiteSpace: 'nowrap' }}>{formatDate(comment?.timestamp)}</TableCell>
            </TableRow>
          ))}
        </CkMuiTable>
      ),
    },
    {
      label: '',
      stopRowClick: true,
      render: (escalation) => (
        <Button style={{ width: '300px' }} variant={'contained'} onClick={() => {
          setEscalationId(escalation?.id);
          setEscalationDialogOpen(true);
        }}>
          Cook Escalation Resolution
        </Button>
      ),
    },
    {
      label: '',
      stopRowClick: true,
      render: (escalation) => (
        <Button style={{ width: '150px' }} variant={'contained'} onClick={() => {
          setEscalationId(escalation?.id);
          setOpen(true);
        }}>
          Update Status
        </Button>
      ),
    },
    {
      label: '',
      stopRowClick: true,
      render: (escalation) => (
        <Button style={{ width: '150px' }} variant={'contained'} onClick={() => {
          setEscalationId(escalation?.id);
          setAddComment(true);
        }}>
          Add Comment
        </Button>
      ),
    },
  ];

  if (!cookEscalationData) return <></>;

  return (
    <Box pt={2}>
      {/* Filter Section — SOS style */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', backgroundColor: 'background.paper' }}>
        <FIlterHeader>
          <FilterSection style={{ marginBottom: '0' }}>
            <SectionHeader>
              <WorkSpaceTitleRow>
                <FilterIcon color="#1976d2" height={20} width={20} />
                <SectionTitle>Filters</SectionTitle>
              </WorkSpaceTitleRow>
            </SectionHeader>
            <TransactionButton
              name="Clear Filter"
              onClickHandler={() => {
                setStatus('');
                setType('');
                setTopicProgressId('');
                setPage(0);
              }}
            />
          </FilterSection>
        </FIlterHeader>
        <FilterContainer>
          <FilterTextField
            select
            label="Status"
            size="small"
            variant="outlined"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Clear Filter</MenuItem>
            {Object.keys(CookEscalationAction).map((e) => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </FilterTextField>

          <FilterTextField
            select
            label="Escalation Type"
            size="small"
            variant="outlined"
            value={type}
            onChange={(e) => { setType(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Clear Filter</MenuItem>
            {Object.keys(CookEscalationResolutions).map((e) => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </FilterTextField>

          <FilterTextField
            select
            label="MANUAL / AUTOMATED"
            size="small"
            variant="outlined"
            value={topicProgressId}
            onChange={(e) => { setTopicProgressId(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Clear Filter</MenuItem>
            <MenuItem value="MANUAL">MANUAL</MenuItem>
            <MenuItem value="AUTOMATED">AUTOMATED</MenuItem>
          </FilterTextField>
        </FilterContainer>
      </Box>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={1} mt={2} mb={2} flexWrap="wrap">
        {[CookWorkStatus.BLACKLISTED].includes(record?.cookFullProfile?.workStatus) && (
          <Button
            disabled={!GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER])}
            onClick={() => setFreeToWork(true)}
            color="secondary"
            style={{ width: 'max-content', fontSize: '12px' }}
            size="small"
            variant="contained"
          >
            Mark Free To Work
          </Button>
        )}
        {![CookWorkStatus.FREE_TO_WORK, CookWorkStatus.BLACKLISTED].includes(record?.cookFullProfile?.workStatus) && (
          <Button
            disabled={!GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER])}
            onClick={() => handleSubmit()}
            color="secondary"
            style={{ width: 'max-content', fontSize: '12px' }}
            size="small"
            variant="contained"
          >
            Mark Free To Work
          </Button>
        )}
        {[CookWorkStatus.FREE_TO_WORK].includes(record?.cookFullProfile?.workStatus) && (
          <Button
            disabled={!GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN, UserGroupsEnum.MANAGER])}
            onClick={() => setMarkNotFreeOpen(true)}
            color="secondary"
            style={{ width: 'max-content', fontSize: '12px' }}
            size="small"
            variant="contained"
          >
            Mark Not Free To Work
          </Button>
        )}
        <Button
          onClick={() => setOpenEscalationDialogBox(true)}
          color="secondary"
          style={{ width: 'max-content', fontSize: '12px' }}
          size="small"
          variant="contained"
        >
          Create Escalation
        </Button>
      </Box>

      {/* List */}
      <CommonListTable
        columns={columns}
        data={cookEscalationData ?? []}
        loading={false}
        page={page}
        rowsPerPage={rowsPerPage}
        total={subData?.count ?? 0}
        onPageChange={setPage}
        onRowsPerPageChange={(rows) => { setRowsPerPage(rows); setPage(0); }}
        emptyText="No Escalations Found"
      />

      {/* Dialogs */}
      <CookEscalationDialog
        cookId={cookId}
        open={escalationDialogBox}
        setOpen={setOpenEscalationDialogBox}
        refresh={refresh}
        onSubmit={() => { loadData(); }}
      />
      <MarkNotFreeForWorkDialog
        cookId={record?.cookFullProfile?.cookId}
        open={markNotFreeOpen}
        onClose={() => setMarkNotFreeOpen(false)}
        onSuccess={() => { loadData(); refresh(); }}
      />

      <Dialog open={notFreeToWorkDialog} onClose={() => { setNotFreeToWorkDialog(false); setSelectedReasoning(''); }}>
        <DialogTitle>Mark Not Free To Work</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1, minWidth: 300 }}>
            <InputLabel>Reasoning</InputLabel>
            <Select
              value={selectedReasoning}
              label="Reasoning"
              onChange={(e) => setSelectedReasoning(e.target.value as CookWorkStatusReasoning)}
            >
              {Object.values(CookWorkStatusReasoning).map((val) => (
                <MenuItem key={val} value={val}>{val.replace(/_/g, ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNotFreeToWorkDialog(false); setSelectedReasoning(''); }}>Cancel</Button>
          <Button
            disabled={!selectedReasoning}
            variant="contained"
            onClick={() => {
              cookService
                .markNotFreeForWork(record?.cookFullProfile?.cookId, { cookWorkStatusReasoning: selectedReasoning as string })
                .then(() => {
                  EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                    message: "Successfully marked not free to work",
                    type: CustomSnackbarTypes.SUCCESS,
                  });
                  setNotFreeToWorkDialog(false);
                  setSelectedReasoning('');
                })
                .catch((err) => { console.log(err); })
                .finally(() => { refresh(); });
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Update Escalation Status</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl style={{ width: 520 }} fullWidth>
                <InputLabel>Select Status</InputLabel>
                <Select
                  autoWidth={false}
                  defaultValue="Select Status"
                  value={escalationType}
                  onChange={(e) => setEscalationType(e.target.value)}
                >
                  {Object.keys(CookEscalationAction).map((item: string) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="contained" onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (escalationType === '' || !escalationId) {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Select a type", type: CustomSnackbarTypes.ERROR });
              return;
            }
            cookService.updateEscalationsStatus(cookId, { escalationId, actionByCook: escalationType }).then(() => {
              refresh();
              loadData();
              setOpen(false);
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Updated Successfully", type: CustomSnackbarTypes.SUCCESS });
            });
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={escalationDialogOpen} onClose={() => setEscalationDialogOpen(false)}>
        <DialogTitle>Update Cook Escalation Resolution Type</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl style={{ width: 520 }} fullWidth>
                <InputLabel>Select Resolution Type</InputLabel>
                <Select
                  autoWidth={false}
                  defaultValue="Select Resolution Type"
                  value={escalationResolution}
                  onChange={(e) => setEscalationResolution(e.target.value)}
                >
                  {Object.keys(CookEscalationResolutionsEnum).map((item: string) => (
                    <MenuItem key={item} value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="contained" onClick={() => setEscalationDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (escalationResolution === '' || !escalationId) {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Select a type", type: CustomSnackbarTypes.ERROR });
              return;
            }
            cookService.patchCookResolution(cookId, escalationId, { escalationId, resolution: escalationResolution }).then(() => {
              refresh();
              loadData();
              setEscalationDialogOpen(false);
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Updated Successfully", type: CustomSnackbarTypes.SUCCESS });
            });
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={freeToWork} onClose={() => setFreeToWork(false)}>
        <DialogTitle>Mark Free To Work</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Comment" fullWidth value={comment} onChange={(e) => setComment(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="contained" onClick={() => setFreeToWork(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (comment === '') {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Enter Details", type: CustomSnackbarTypes.ERROR });
              return;
            }
            handleSubmit();
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addComment} onClose={() => setAddComment(false)}>
        <DialogTitle>Add comment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Comment" fullWidth value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" variant="contained" onClick={() => setAddComment(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            if (commentText === '' || !escalationId) {
              EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Enter Details to continue", type: CustomSnackbarTypes.ERROR });
              return;
            }
            cookService
              .addComment(record?.cookFullProfile?.cookId, escalationId, { comment: commentText })
              .then(() => {
                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: "Comment added Successfully", type: CustomSnackbarTypes.SUCCESS });
                setAddComment(false);
                setCommentText('');
                loadData();
              })
              .catch((err) => { console.log(err); })
              .finally(() => { refresh(); });
          }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CookEscalation;
